import React, { Component } from 'react'
import { connect } from 'react-redux'
import { Field, reduxForm, formValueSelector, FormErrors } from 'redux-form'
import { withRouter, Redirect } from 'react-router-dom'
import { AssertionError } from 'assert'

import dropZone from './dropzoneInput'
import { Document } from './types'
import { Refi } from '../features/refiApp/types'
import { SelectField, InputField } from '../components/inputs'
import { ReduxFormProps } from '../forms'

export type Callback<T = void> = (...args: any) => T
export interface Match<T> {
  params: T
}

export interface Params {
  usertype: string
}

export interface Props {
  match: Match<Params>
  refiTransaction: Refi
  selectedDocumentType: string
  onSubmit: (value: any) => Promise<void>
  clearFields: Callback
}

export interface FormProps {
  documentType: string
  uploaded_document: string
  description: string
}

// eslint-disable-next-line
export class DocumentUploadForm extends Component<
  ReduxFormProps<Props, FormProps>
> {
  static displayName = 'documentUploadForm'

  state = {
    isSuccessfulUpload: false,
  }

  getBase64 = (file): Promise<string | ArrayBuffer | null> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file[0] ? file[0] : file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = (error) => reject(error)
    })
  }

  generateDocumentType = (data: { documentType: string }) => {
    const { refiTransaction } = this.props
    if (
      data.documentType === 'DRIVERS_LICENSE' ||
      data.documentType === 'PROOF_OF_INCOME' ||
      data.documentType === 'PROOF_OF_INSURANCE'
    ) {
      return 'USER'
    } else if (refiTransaction) {
      return 'REFI'
    }

    throw new AssertionError({ message: 'unknown document type' })
  }

  generateDocumentId = (data) => {
    const { refiTransaction } = this.props
    if (
      data.documentType === 'DRIVERS_LICENSE' ||
      data.documentType === 'PROOF_OF_INCOME' ||
      data.documentType === 'PROOF_OF_INSURANCE'
    ) {
      if (refiTransaction) {
        return refiTransaction.user.id
      }
    } else if (refiTransaction) {
      return refiTransaction.id
    }

    throw new AssertionError({ message: 'unknown document id' })
  }

  uploadImage = (data) => {
    const { onSubmit } = this.props

    let document: Document = {
      type: data.documentType,
      description: data.description,
      imageableId: this.generateDocumentId(data),
      imageableType: this.generateDocumentType(data),
    }

    this.getBase64(data.uploaded_document).then((res) => {
      document.image = res
      return onSubmit(document)
        .then(() => {
          this.setState({
            isSuccessfulUpload: true,
          })
        })
        .catch(() => {
          this.setState({
            isSuccessfulUpload: false,
          })
        })
    })
  }

  deleteFile = () => {
    this.props.clearFields(
      'documentUploadForm',
      false,
      false,
      'uploaded_document'
    )
  }

  redirectUrl = () => {
    const { refiTransaction } = this.props
    if (refiTransaction) {
      return `/refinance/${refiTransaction.id}`
    } else {
      return null
    }
  }

  render() {
    let documentTypeOptions
    const {
      handleSubmit,
      pristine,
      submitting,
      selectedDocumentType,
      refiTransaction,
      match,
    } = this.props

    if (this.state.isSuccessfulUpload) {
      return <Redirect to={this.redirectUrl()} />
    }

    const optionsForAllTypes = [
      { label: "Driver's License", value: 'DRIVERS_LICENSE' },
      { label: 'Registration', value: 'REGISTRATION' },
      { label: 'Other', value: 'OTHER_DOCUMENT' },
    ]

    const optionsForRefiAndTransactions = [
      { label: 'Proof of insurance', value: 'PROOF_OF_INSURANCE' },
      { label: 'Proof of income', value: 'PROOF_OF_INCOME' },
    ]

    const optionsForRefi = [
      { label: 'Mileage Verification', value: 'MILEAGE_VERIFICATION' },
    ]

    if (refiTransaction) {
      documentTypeOptions = [
        ...optionsForAllTypes,
        ...optionsForRefiAndTransactions,
        ...optionsForRefi,
      ]
    }

    return (
      <div className="document-upload-form">
        <h1 className="document-upload-form__header">Upload a document</h1>
        <form
          onSubmit={handleSubmit(this.uploadImage)}
          className="document-upload-form__form"
          data-test="document-upload-form"
        >
          <div className="document-upload-form__type">
            <Field<any> // TOOD-TS
              label="Select a type"
              name="documentType"
              component={SelectField}
              aria-required="true"
              gridClass="filter__field"
            >
              <option value="">No type selected</option>
              {(refiTransaction || match.params.usertype === 'buyer') && (
                <option value="APP_FOR_TITLE">Application for Title</option>
              )}
              {refiTransaction &&
                documentTypeOptions.map((option) => {
                  return (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  )
                })}
            </Field>
            {selectedDocumentType === 'OTHER_DOCUMENT' && (
              <Field
                type="description"
                label="Description"
                name="description"
                id="description"
                placeholder=""
                component={InputField}
              />
            )}
            <button
              className="button button--primary document-upload-form__button"
              type="submit"
              disabled={pristine || submitting}
            >
              Upload file
            </button>
          </div>
          <div className="document-upload-form__file">
            <Field
              type="file"
              name="uploaded_document"
              component={dropZone}
              deleteFile={this.deleteFile}
            />
          </div>
        </form>
      </div>
    )
  }
}

const validate = (data): FormErrors<FormProps, string> => {
  const { documentType, uploaded_document, description } = data
  let errors: Partial<FormProps> = {}

  if (!documentType) {
    errors.documentType = 'Please select a document type'
  }
  if (!uploaded_document) {
    errors.uploaded_document = 'Please select a file to upload'
  }
  if (!description) {
    errors.description = 'Please fill in the description'
  }
  if (description && description.length > 50) {
    errors.description = 'Must be 50 characters or less'
  }
  return errors
}

const DocumentUploadFormWithRouter = reduxForm<FormProps, Props>({
  form: 'documentUploadForm',
  validate,
})(DocumentUploadForm)

const selector = formValueSelector('documentUploadForm')
const DocumentUploadFormConnected = connect((state) => {
  const selectedDocumentType = selector(state, 'documentType')
  return { selectedDocumentType }
})(DocumentUploadFormWithRouter)

export default withRouter(DocumentUploadFormConnected)
